import { useEffect, useRef, useState, type ReactNode } from 'react'
import { createPortal } from 'react-dom'
import { useTranslation } from 'react-i18next'
import { useOptionalAuth } from '../../context/AuthContext'
import { useIsNarrow } from '../../hooks/useIsNarrow'
import { isSupportedLanguage, SUPPORTED_LANGUAGES, type SupportedLanguage } from '../../i18n'
import { useLibraryDeps } from '../../library/LibraryDeps'
import { isLightTheme, useTheme } from '../../theme/themes'
import { Avatar } from '../Avatar'
import { nextTheme, toMenuUser, type MenuUser } from '../more/menuData'
import { MENU_ICONS } from '../more/menuIcons'
import { createOfApi, OF } from '../ofApi'
import { compact } from '../posts'
import '../../library/library.css'
import '../ofPages.css'
import './banking.css'

interface MenuProps {
  accountId: string
  /** Whether this login may open a CRM page of this Account (its CRM feature, owner-only pages). */
  canOpen: (page: string) => boolean
  onOpenPage: (page: string, query?: string) => void
}

interface Props extends MenuProps {
  mobileNav?: ReactNode
}

/** One entry of OnlyFans' sidebar, in its order: the CRM page it opens (shown only when the login may open it). */
interface Entry {
  page: string
  icon: string
  label: string
  /** Grey words after the label: "(to subscribe)". */
  hint?: string
}

const MAIN: Entry[] = [
  { page: 'profile', icon: 'icon-profile-round', label: 'myProfile' },
  { page: 'referrals', icon: 'icon-referrals', label: 'referrals' },
  { page: 'discover', icon: 'ic-discover', label: 'discover' },
  { page: 'collections', icon: 'icon-collections', label: 'collections' },
  { page: 'queue', icon: 'icon-calendar-datepicker', label: 'queue' },
  { page: 'vault', icon: 'icon-vault', label: 'vault' },
  { page: 'settings', icon: 'icon-settings', label: 'settings' },
  { page: 'streaming', icon: 'icon-live', label: 'streaming' },
  { page: 'statements', icon: 'statements', label: 'statements' },
  { page: 'statistics', icon: 'icon-statements', label: 'statistics' },
  { page: 'releaseforms', icon: 'icon-account', label: 'releaseForms' },
]

const MONEY: Entry[] = [
  { page: 'cards', icon: 'icon-card', label: 'yourCards', hint: 'toSubscribe' },
  { page: 'banking', icon: 'icon-bank', label: 'bank', hint: 'toEarn' },
]

/** Each language in its own words, as OnlyFans' language picker lists them. */
const NATIVE_NAME: Record<SupportedLanguage, string> = { en: 'English', uk: 'Українська', ru: 'Русский', es: 'Español' }

/** The last users/me per account for this session: going back to More shows it at once. */
const lastUser = new Map<string, MenuUser>()

function Glyph({ id, className = 'ofmore-icon' }: { id: string; className?: string }) {
  const icon = MENU_ICONS[id]
  if (!icon) return null
  // Static markup copied from OnlyFans' sprite, not user data.
  return <svg className={className} viewBox={icon.viewBox} fill="currentColor" aria-hidden="true" dangerouslySetInnerHTML={{ __html: icon.body }} />
}

/**
 * More as a page: the phones' Menu tab (and any link to /crm/more), the menu
 * filling the column with the tab bar under it.
 */
export function MorePage({ mobileNav, ...menu }: Props) {
  const mobile = useIsNarrow()
  return (
    <div className={`lib ofp ofmore ${mobile ? 'lib--m ofmore--m' : ''}`}>
      <main className="lib-main">
        <div className="ofmore-scroll">
          <MoreMenu {...menu} />
        </div>
      </main>
      {mobile && mobileNav}
    </div>
  )
}

/**
 * More as OnlyFans opens it from its left bar on a computer: a 248px card over
 * the page, darkened behind it; the ×, a click outside or Esc closes it, and so
 * does opening a page from it.
 */
export function MorePopup({ onClose, onOpenPage, left, ...menu }: MenuProps & { onClose: () => void; /** The card's left edge: just past the rail. */ left: number }) {
  const { t } = useTranslation('ofMore')
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose()
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [onClose])
  return createPortal(
    <div className="ofmore ofmore-pop" role="dialog" aria-modal="true" aria-label={t('more')}>
      <div className="ofmore-pop-backdrop" onClick={onClose} />
      <div className="ofmore-pop-card" style={{ left }}>
        <button type="button" className="ofmore-pop-close" onClick={onClose} aria-label={t('close')} title={t('close')}>
          <Glyph id="icon-close" />
        </button>
        <MoreMenu
          {...menu}
          avatarSize={48}
          onOpenPage={(page, query) => {
            onClose()
            onOpenPage(page, query)
          }}
        />
      </div>
    </div>,
    document.body,
  )
}

/**
 * OnlyFans' left-bar sidebar menu (onlyfans.com's l-sidebar) — the account,
 * its Fans / Following, then every page in OnlyFans' order, each opening our
 * copy of it. Dark mode and the language are the app's own. "Log out" is left
 * out: it would sign the account out of OnlyFans and stop the AI.
 */
function MoreMenu({ accountId, canOpen, onOpenPage, avatarSize = 50 }: MenuProps & { avatarSize?: number }) {
  const { t, i18n } = useTranslation('ofMore')
  const { gateway } = useLibraryDeps()
  const auth = useOptionalAuth()
  const [theme, setTheme] = useTheme()
  const [user, setUser] = useState<MenuUser | null>(() => lastUser.get(accountId) ?? null)
  const [langOpen, setLangOpen] = useState(false)
  const langRef = useRef<HTMLDivElement | null>(null)

  useEffect(() => {
    let live = true
    createOfApi(gateway)
      .get('users/me', {}, `${OF}/`)
      .then((raw) => {
        const me = toMenuUser(raw)
        if (!live || !me) return
        lastUser.set(accountId, me)
        setUser(me)
      })
      // No users/* for this role, or OnlyFans did not answer: the menu works without the header.
      .catch(() => {})
    return () => {
      live = false
    }
  }, [gateway, accountId])

  useEffect(() => {
    if (!langOpen) return
    const onDown = (e: MouseEvent) => {
      if (!langRef.current?.contains(e.target as Node)) setLangOpen(false)
    }
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && setLangOpen(false)
    document.addEventListener('mousedown', onDown)
    document.addEventListener('keydown', onKey)
    return () => {
      document.removeEventListener('mousedown', onDown)
      document.removeEventListener('keydown', onKey)
    }
  }, [langOpen])

  const language: SupportedLanguage = auth?.language ?? (isSupportedLanguage(i18n.language) ? i18n.language : 'en')
  const pickLanguage = (next: SupportedLanguage) => {
    setLangOpen(false)
    if (next === language) return
    // Saved on the person's profile (the whole app follows); outside sign-in just this page's i18n.
    if (auth) void auth.setLanguage(next).catch(() => {})
    else void i18n.changeLanguage(next)
  }
  const dark = !isLightTheme(theme)
  const switchTheme = () => {
    const next = nextTheme(theme)
    if (auth) void auth.saveStyling({ theme: next }).catch(() => {})
    else setTheme(next)
  }

  const item = (e: Entry) =>
    canOpen(e.page) && (
      <button key={e.page} type="button" className="ofmore-item" onClick={() => onOpenPage(e.page)}>
        <Glyph id={e.icon} />
        <span className="ofmore-text">
          {t(e.label)}
          {e.hint && <span className="ofmore-hint"> {t(e.hint)}</span>}
        </span>
      </button>
    )
  const money = MONEY.filter((e) => canOpen(e.page))
  const canCollections = canOpen('collections')
  const canProfile = canOpen('profile')

  return (
    <nav className="ofmore-menu" aria-label={t('more')}>
      {user && (
        <div className="ofmore-user">
          <button type="button" className="ofmore-avatar" disabled={!canProfile} onClick={() => onOpenPage('profile')} aria-label={t('myProfile')}>
            <Avatar user={{ name: user.name, avatar: user.avatar }} size={avatarSize} />
          </button>
          <div className="ofmore-name">
            <span className="ofmore-name-text">{user.name}</span>
            {user.verified && <Glyph id="icon-verified" className="ofmore-verified" />}
          </div>
          <div className="ofmore-username">@{user.username}</div>
          <div className="ofmore-counts">
            <button type="button" disabled={!canCollections} onClick={() => onOpenPage('collections', 'view=fans')}>
              <strong>{compact(user.fans)}</strong> {t('fans')}
            </button>
            <span className="ofmore-dot" aria-hidden="true">
              •
            </span>
            <button type="button" disabled={!canCollections} onClick={() => onOpenPage('collections', 'view=following')}>
              <strong>{compact(user.following)}</strong> {t('following')}
            </button>
          </div>
        </div>
      )}
      <div className="ofmore-group">{MAIN.map(item)}</div>
      {money.length > 0 && <div className="ofmore-group">{money.map(item)}</div>}
      <div className="ofmore-group">
        <a className="ofmore-item" href={`${OF}/help`} target="_blank" rel="noopener noreferrer">
          <Glyph id="icon-support" />
          <span className="ofmore-text">{t('helpAndSupport')}</span>
        </a>
        <button type="button" className="ofmore-item" onClick={switchTheme}>
          <Glyph id={dark ? 'icon-theme-light' : 'icon-theme-dark'} />
          <span className="ofmore-text">{t(dark ? 'lightMode' : 'darkMode')}</span>
        </button>
        <div className={`ofmore-lang ${langOpen ? 'is-open' : ''}`} ref={langRef}>
          <button type="button" className="ofmore-item" aria-haspopup="listbox" aria-expanded={langOpen} onClick={() => setLangOpen((o) => !o)}>
            <Glyph id="icon-language" />
            <span className="ofmore-text">{NATIVE_NAME[language]}</span>
            <Glyph id="icon-arrow-down" className="ofmore-arrow" />
          </button>
          {langOpen && (
            <ul className="ofmore-lang-list" role="listbox" aria-label={t('language')}>
              {SUPPORTED_LANGUAGES.map((id) => (
                <li key={id} role="option" aria-selected={id === language}>
                  <button type="button" className={id === language ? 'is-on' : ''} onClick={() => pickLanguage(id)}>
                    {NATIVE_NAME[id]}
                  </button>
                </li>
              ))}
            </ul>
          )}
        </div>
      </div>
    </nav>
  )
}
